import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  IconButton,
  Tooltip,
  Grid,
  Paper
} from '@mui/material'
import {
  Folder as FolderIcon,
  Image as ImageIcon,
  ArrowBack as BackIcon,
  CreateNewFolder as NewFolderIcon,
  Delete as DeleteIcon,
  Close as CloseIcon
} from '@mui/icons-material'
import { useLanguage } from '../../contexts/LanguageContext'
import { listExplorer, createFolder, deleteItem, ExplorerItem } from '../../api/explorer'

interface ExplorerModalProps {
  open: boolean
  onClose: () => void
  onSelect: (imageUrl: string) => void
}

const ExplorerModal: React.FC<ExplorerModalProps> = ({ open, onClose, onSelect }) => {
  const { t } = useLanguage()

  const [currentPath, setCurrentPath] = useState<string>('')
  const [items, setItems] = useState<ExplorerItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [newFolderName, setNewFolderName] = useState('')
  const [showNewFolder, setShowNewFolder] = useState(false)
  const [selectedItem, setSelectedItem] = useState<ExplorerItem | null>(null)

  // Load items when path changes or modal opens
  useEffect(() => {
    if (open) {
      loadItems(currentPath)
    }
  }, [open, currentPath])

  const loadItems = async (path?: string) => {
    try {
      setLoading(true)
      setError(null)
      const response = await listExplorer(path)

      if (response.success) {
        setCurrentPath(response.path)
        setItems(response.items)
      } else {
        setError(response.message || t('easyWay.explorer.errorLoad'))
      }
    } catch (err: any) {
      console.error('Explorer load error:', err)
      setError(err.response?.data?.message || err.message || t('easyWay.explorer.errorLoad'))
    } finally {
      setLoading(false)
    }
  }

  const handleItemClick = (item: ExplorerItem) => {
    if (item.type === 'folder') {
      setCurrentPath(item.path || '')
      setSelectedItem(null)
    } else {
      setSelectedItem(item)
    }
  }

  const handleBack = () => {
    // Go up one level
    const parts = currentPath.split('/').filter(p => p)
    if (parts.length > 2) {  // Keep at least output/username/
      parts.pop()
      setCurrentPath(parts.join('/') + '/')
    }
  }

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return

    try {
      setError(null)
      const response = await createFolder(currentPath, newFolderName.trim())

      if (response.success) {
        setNewFolderName('')
        setShowNewFolder(false)
        await loadItems(currentPath)  // Reload
      } else {
        setError(response.message)
      }
    } catch (err: any) {
      console.error('Create folder error:', err)
      setError(err.response?.data?.message || err.message || t('easyWay.explorer.errorCreate'))
    }
  }

  const handleDelete = async (item: ExplorerItem) => {
    if (!confirm(t('easyWay.explorer.confirmDelete').replace('{name}', item.name))) {
      return
    }

    try {
      setError(null)
      const path = item.type === 'folder' ? item.path! : `${currentPath}${item.name}`
      const response = await deleteItem(path, item.type)

      if (response.success) {
        await loadItems(currentPath)  // Reload
        if (selectedItem?.name === item.name) {
          setSelectedItem(null)
        }
      } else {
        setError(response.message)
      }
    } catch (err: any) {
      console.error('Delete error:', err)
      setError(err.response?.data?.message || err.message || t('easyWay.explorer.errorDelete'))
    }
  }

  const handleSelectImage = () => {
    if (selectedItem && selectedItem.url) {
      onSelect(selectedItem.url)
      onClose()
    }
  }

  const getBreadcrumbs = () => {
    const parts = currentPath.split('/').filter(p => p)
    return parts
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">{t('easyWay.explorer.title')}</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Breadcrumb navigation */}
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={t('easyWay.explorer.back')}>
            <IconButton
              onClick={handleBack}
              disabled={currentPath.split('/').filter(p => p).length <= 2}
              size="small"
            >
              <BackIcon />
            </IconButton>
          </Tooltip>

          <Breadcrumbs maxItems={4}>
            {getBreadcrumbs().map((part, idx) => (
              <Typography key={idx} variant="body2" color="text.secondary">
                {part}
              </Typography>
            ))}
          </Breadcrumbs>
        </Box>

        {/* Toolbar */}
        <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
          {!showNewFolder && (
            <Button
              startIcon={<NewFolderIcon />}
              onClick={() => setShowNewFolder(true)}
              size="small"
              variant="outlined"
            >
              {t('easyWay.explorer.newFolder')}
            </Button>
          )}
        </Box>

        {/* New folder input */}
        {showNewFolder && (
          <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
            <TextField
              size="small"
              fullWidth
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder={t('easyWay.explorer.folderName')}
              onKeyPress={(e) => {
                if (e.key === 'Enter') handleCreateFolder()
              }}
            />
            <Button onClick={handleCreateFolder} variant="contained" size="small">
              {t('easyWay.explorer.create')}
            </Button>
            <Button onClick={() => { setShowNewFolder(false); setNewFolderName('') }} size="small">
              {t('easyWay.explorer.cancel')}
            </Button>
          </Box>
        )}

        {/* Error message */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Two-column layout: file list + preview */}
        <Grid container spacing={2} sx={{ minHeight: 400 }}>
          {/* Left column: Items list */}
          <Grid item xs={12} md={6}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : items.length === 0 ? (
              <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                {t('easyWay.explorer.empty')}
              </Typography>
            ) : (
              <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                {items.map((item, idx) => (
                  <ListItem
                    key={idx}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        onClick={() => handleDelete(item)}
                        size="small"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    }
                    disablePadding
                  >
                    <ListItemButton
                      onClick={() => handleItemClick(item)}
                      selected={selectedItem?.name === item.name && item.type === 'file'}
                    >
                      <ListItemIcon>
                        {item.type === 'folder' ? <FolderIcon color="primary" /> : <ImageIcon color="action" />}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.name}
                        secondary={
                          item.type === 'file' && item.size
                            ? formatFileSize(item.size)
                            : undefined
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </Grid>

          {/* Right column: Image preview */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                height: 400,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'action.hover',
                borderRadius: 1,
                overflow: 'hidden'
              }}
            >
              {selectedItem && selectedItem.type === 'file' && selectedItem.url ? (
                <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
                  <img
                    src={selectedItem.url}
                    alt={selectedItem.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain'
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      position: 'absolute',
                      bottom: 8,
                      left: 8,
                      right: 8,
                      backgroundColor: 'rgba(0, 0, 0, 0.6)',
                      color: 'white',
                      padding: '4px 8px',
                      borderRadius: 1,
                      textAlign: 'center'
                    }}
                  >
                    {selectedItem.name}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('easyWay.explorer.noPreview')}
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          {t('easyWay.explorer.close')}
        </Button>
        <Button
          onClick={handleSelectImage}
          disabled={!selectedItem || selectedItem.type !== 'file'}
          variant="contained"
        >
          {t('easyWay.explorer.selectImage')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ExplorerModal
